import type { JsonObject, JsonValue } from "src/components/views/ai/assistant/uiContext/jsonTypes.ts";

export const AGENT_FORM_REDACTED_INPUT_VALUE = "[REDACTED]";

/**
 * Replaces the values at the given dot-separated input paths, such as `apiKey` or `parameters.0.value`, so secrets
 * never reach the agent. Empty values are kept, so the agent can still tell whether a secret has been entered.
 */
export function redactAgentFormInputValues(inputs: JsonObject, secretPaths: Iterable<string>): JsonObject {
	const secretPathSet = new Set(secretPaths);
	if (secretPathSet.size === 0) return inputs;
	return redactJsonObject(inputs, "", secretPathSet);
}

function redactJsonValue(value: JsonValue, path: string, secretPaths: ReadonlySet<string>): JsonValue {
	if (secretPaths.has(path)) {
		return isEmptyValue(value) ? value : AGENT_FORM_REDACTED_INPUT_VALUE;
	}
	if (Array.isArray(value)) {
		return value.map((entryValue, index) =>
			redactJsonValue(entryValue, childPath(path, String(index)), secretPaths),
		);
	}
	if (value !== null && typeof value === "object") {
		return redactJsonObject(value, path, secretPaths);
	}
	return value;
}

function redactJsonObject(value: JsonObject, path: string, secretPaths: ReadonlySet<string>): JsonObject {
	const result: JsonObject = {};
	for (const [key, entryValue] of Object.entries(value)) {
		result[key] = redactJsonValue(entryValue, childPath(path, key), secretPaths);
	}
	return result;
}

function childPath(path: string, key: string): string {
	return path === "" ? key : `${path}.${key}`;
}

function isEmptyValue(value: JsonValue): boolean {
	return value === null || value === "";
}
